import { Plus, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../../components/modal/Modal";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import { useBudgetCategoryStore } from "../../../store/store";
import { themeQuartz } from "ag-grid-community";
import images from "../../../assets/images";
import { AgGridReact } from "ag-grid-react";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

type budgetCatFormData = {
  categoryId: string;
  categoryName: string;
};

const emptyForm: budgetCatFormData = { categoryId: "", categoryName: "" };

const BudgetCategories: React.FC = () => {
  const [rowData, setRowData] = useState<budgetCatFormData[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);

  const {
    budgetCategories,
    fetchBudgetCategories,
    createBudgetCategories,
    updateBudgetCategories,
    deleteBudgetCategories,
  } = useBudgetCategoryStore();

  const [formData, setFormData] = useState<budgetCatFormData>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);

  useEffect(() => {
    const budgetCatData = budgetCategories.map((cat: any) => {
      return {
        ...cat,
      };
    });
    setRowData(budgetCatData);
  }, [budgetCategories]);

  useEffect(() => {
    fetchBudgetCategories();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEdit = (data: any) => {
    setFormData({
      categoryId: data.categoryId,
      categoryName: data.categoryName,
    });
    setIsOpen(true);
    setEditId(data._id);
  };

  const handleDelete = (id: string) => {
    setCategoryToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (categoryToDelete) {
      await deleteBudgetCategories(categoryToDelete);
      setIsDeleteModalOpen(false);
      setCategoryToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setCategoryToDelete(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editId) {
      await updateBudgetCategories(editId, formData);
    } else {
      await createBudgetCategories(formData);
    }
    setFormData(emptyForm);
    setEditId(null);
    setIsOpen(false);
  };

  const [columnDefs, setColDefs] = useState<any>([
    {
      field: "categoryId",
      headerName: "Category ID",
      sortable: false,
      // minWidth: 20,
      minWidth: 30,
      maxWidth: 150,
      flex: 0,
      headerStyle: {
        backgroundColor: "#e9f5ff",
      },
      filter: false,
      cellStyle: {
        textAlign: "left",
        padding: "10px 0 0 15px",
      },
    },
    {
      field: "categoryName",
      headerName: "Category Name",
      sortable: false,
      minWidth: 20,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 5px 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "categoryName",
    },
    {
      field: "createdDate",
      headerName: "Created At",
      sortable: false,
      minWidth: 20,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 5px 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      valueGetter: (params: any) => {
        return (
          new Date(params.data?.createdDate).toLocaleDateString("en-US") || "NA"
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      cellStyle: { padding: "10px 15px 0 0 ", textAlign: "left" },
      // minWidth: 20,
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 0,
      minWidth: 40,
      maxWidth: 90,
      cellRenderer: (params: any) => (
        <div className="flex gap-x-3 pt-2">
          <button
            onClick={() => handleEdit(params.data)}
            className="p-1 hover:bg-gray-100 rounded"
            title="Edit BudgetCategory"
          >
            <img className="iconTd w-5 h-5" src={images.editIcon} alt="edit" />
          </button>
          <button
            onClick={() => handleDelete(params.data._id)}
            className="p-1 hover:bg-red-100 rounded"
            title="Delete BudgetCategory"
          >
            <Trash2 className="text-red-500" size={23} />
          </button>
        </div>
      ),
      filter: false,
    },
  ]);

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
      },
    };
  }, []);

  const theme = useMemo(() => {
    return gridTheme;
  }, []);

  return (
    <>
      <div className="p-3 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Budget Categories</h2>
          <button
            onClick={() => {
              setIsOpen(true);
              setFormData(emptyForm);
              setEditId(null);
            }}
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-3 py-2 rounded-full"
          >
            <Plus size={20} /> Add Category
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  // loading={isLoading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  No Categories added yet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        closeButton={false}
        modalClass={"bg-white w-full max-w-xl rounded-xl relative"}
      >
        {
          <>
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">
                {"Budget Category"}
              </h2>
              <div className="pr-5 mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category ID
                </label>
                <input
                  type="text"
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  placeholder="Enter Category ID"
                  className="w-75 px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#375998]"
                />
              </div>
              <div className="pr-5 mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name
                </label>
                <input
                  type="text"
                  name="categoryName"
                  value={formData.categoryName}
                  onChange={handleChange}
                  placeholder="Enter Category Name"
                  className="w-75 px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#375998]"
                />
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-3 p-2 bg-[#5A7192] w-full rounded-b-lg">
              <button
                onClick={() => {
                  setIsOpen(false);
                  setFormData(emptyForm);
                }}
                className="bg-[#8497B0] font-semibold text-white px-4 py-2 rounded-full hover:opacity-90"
              >
                CANCEL
              </button>
              <button
                onClick={handleSubmit}
                className="bg-[#00A8A8] font-semibold text-white px-4 py-2 rounded-full hover:opacity-90"
              >
                SUBMIT
              </button>
            </div>
          </>
        }
      </Modal>
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="Are you sure you want to delete this category?"
        confirmLabel="YES"
        cancelLabel="NO"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

export default BudgetCategories;
